import { useEffect, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { GridItem } from '../GridItem';

/**
 * Formats a cost in cents as a dollar string, e.g. 15000 -> "$150.00"
 */
function formatCost(cents: number, unit?: string): string {
  const dollars = `$${(cents / 100).toFixed(2)}`;
  return unit ? `${dollars} / ${unit}` : dollars;
}

/**
 * Extracts addon cost in cents from an entity's annotations.
 * Returns 0 if not present or invalid.
 */
function getAddonCostCents(entity: Entity): number {
  const raw =
    entity.metadata.annotations?.['heroku.com/addon-billed-price-cents'];
  const cents = Number(raw);
  return cents > 0 ? cents : 0;
}

/**
 * Shows cost for a single heroku-addon entity from its own annotations.
 */
const SingleAddonCost = ({ entity }: { entity: Entity }) => {
  const cents = getAddonCostCents(entity);
  if (cents <= 0) return null;

  const unit =
    entity.metadata.annotations?.['heroku.com/addon-billed-price-unit'];
  return (
    <GridItem
      label="Cost"
      value={formatCost(cents, unit)}
      gridSizes={{ xs: 12, sm: 4 }}
    />
  );
};

/**
 * Fetches all addon entities related to a heroku-app (via hasPart relations)
 * and displays the summed cost.
 */
const AggregatedAddonCost = ({ entity }: { entity: Entity }) => {
  const catalogApi = useApi(catalogApiRef);
  const [totalCents, setTotalCents] = useState<number>(0);
  const [unit, setUnit] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchAddonCosts() {
      const addonRelTypes = new Set(['haspart', 'heroku-hasaddon']);
      const addonRefs = (entity.relations || [])
        .filter(r => addonRelTypes.has(r.type.toLocaleLowerCase('en-US')))
        .map(r => r.targetRef);

      if (addonRefs.length === 0) {
        setLoading(false);
        return;
      }

      let sumCents = 0;
      let billingUnit: string | undefined;

      // Fetch each related entity to read its billing annotations
      const results = await Promise.allSettled(
        addonRefs.map(ref => catalogApi.getEntityByRef(ref)),
      );

      for (const result of results) {
        if (result.status !== 'fulfilled' || !result.value) continue;
        const addonEntity = result.value;
        if (addonEntity.spec?.type !== 'heroku-addon') continue;

        const cents = getAddonCostCents(addonEntity);
        sumCents += cents;
        if (!billingUnit) {
          billingUnit =
            addonEntity.metadata.annotations?.[
              'heroku.com/addon-billed-price-unit'
            ];
        }
      }

      if (!cancelled) {
        setTotalCents(sumCents);
        setUnit(billingUnit);
        setLoading(false);
      }
    }

    fetchAddonCosts();
    return () => {
      cancelled = true;
    };
  }, [entity, catalogApi]);

  if (loading || totalCents <= 0) return null;

  return (
    <GridItem
      label="Addons Cost"
      value={formatCost(totalCents, unit)}
      gridSizes={{ xs: 12, sm: 4 }}
    />
  );
};

/**
 * Displays addon cost in the About card.
 *
 * - For a heroku-addon resource: shows the cost of that single addon.
 * - For a heroku-app resource: fetches all related addon entities via the
 *   catalog API and shows the total cost across all addons.
 *
 * Gated behind the `heroku.features.showCosts` config flag (default: false).
 * Set to `true` in `app-config.yaml` to enable cost display.
 */
export const AddonCostField = ({ entity }: { entity: Entity }) => {
  const config = useApi(configApiRef);
  const showCosts =
    config.getOptionalString('heroku.features.showCosts') === 'true';

  if (!showCosts) return null;

  const specType =
    typeof entity.spec?.type === 'string' ? entity.spec.type : '';

  if (specType === 'heroku-addon') {
    return <SingleAddonCost entity={entity} />;
  }

  if (specType === 'heroku-app') {
    return <AggregatedAddonCost entity={entity} />;
  }

  return null;
};
